// F-070 : demandes d'autorisation du locataire et réponse écrite tracée du bailleur. Le silence ne vaut pas accord.
import { useState } from "react";
import { post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, dateTime } from "@core/format";
import { useOrg } from "@core/org";
import { Button, Card, Checkbox, Empty, ErrorBox, FileDrop, Input, Loading, Modal, Notice, Select, Textarea, fieldError } from "@core/ui";
import { AUTH_KINDS, AUTH_STATUS, DocLinks, RuleNote, Status, meLease, options, orgApi, sendFile, useAction } from "./shared";

function RequestCard({ r, actions }: { r: any; actions?: React.ReactNode }) {
  return (
    <article className="card stack-sm">
      <div className="row-between">
        <div>
          <strong>{r.title}</strong>
          <p className="small muted">
            {AUTH_KINDS[r.kind]} · {r.property_label} · demandée par {r.requester_label} le {dateTime(r.created_at)}
          </p>
        </div>
        <Status value={r.status} labels={AUTH_STATUS} />
      </div>
      <p className="vie-timeline-text">{r.description}</p>
      <DocLinks docs={r.attachments} />
      {r.decided_at ? (
        <div className="notice notice-info">
          <strong className="block">
            Réponse de {r.decided_label} le {dateTime(r.decided_at)}
          </strong>
          <p className="vie-timeline-text">{r.response}</p>
          {r.conditions ? <p>Conditions : {r.conditions}</p> : null}
          {r.valid_until ? <p>Valable jusqu’au {date(r.valid_until)}</p> : null}
          {r.requires_amendment ? <p>Un avenant au bail est nécessaire.</p> : null}
        </div>
      ) : r.status === "en_attente" ? (
        <p className="small muted">Sans réponse écrite du bailleur, la demande n’est pas accordée.</p>
      ) : null}
      <RuleNote>{r.rule_note}</RuleNote>
      {actions}
    </article>
  );
}

/** Liste côté bailleur, avec réponse écrite (rôle qui décide). */
export function OrgAuthorizations({ orgId, leaseId }: { orgId: string; leaseId?: string }) {
  const org = useOrg();
  const path = orgApi(orgId, `/authorization-requests${leaseId ? `?lease_id=${leaseId}` : ""}`);
  const q = useGet<{ items: any[]; rappel: string }>(path);
  const invalidate = useInvalidate();
  const [answer, setAnswer] = useState<any>(null);
  const act = useAction();
  return (
    <div className="stack">
      <Notice tone="info">{q.data?.rappel ?? "Sans réponse écrite du bailleur, la demande n’est pas accordée."}</Notice>
      {q.isLoading ? (
        <Loading />
      ) : q.data?.items.length ? (
        q.data.items.map((r) => (
          <RequestCard
            key={r.id}
            r={r}
            actions={
              r.status === "en_attente" && org.can("decide") ? (
                <div>
                  <Button small onClick={() => setAnswer({ id: r.id, title: r.title, decision: "accordee", response: "", conditions: "", valid_until: "", requires_amendment: false })}>
                    Répondre par écrit
                  </Button>
                </div>
              ) : null
            }
          />
        ))
      ) : (
        <Empty title="Aucune demande d’autorisation" />
      )}
      <Modal
        open={!!answer}
        onClose={() => setAnswer(null)}
        title={answer ? `Réponse : ${answer.title}` : "Réponse"}
        footer={
          <Button
            busy={act.busy}
            onClick={async () => {
              const r = await act.run(
                () =>
                  post(orgApi(orgId, `/authorization-requests/${answer.id}/decision`), {
                    decision: answer.decision,
                    response: answer.response,
                    conditions: answer.conditions,
                    valid_until: answer.valid_until || undefined,
                    requires_amendment: answer.requires_amendment,
                  }),
                "Réponse envoyée au locataire.",
              );
              if (r) {
                setAnswer(null);
                invalidate(orgApi(orgId, "/authorization-requests"));
              }
            }}
          >
            Envoyer la réponse
          </Button>
        }
      >
        {answer ? (
          <div className="stack">
            <ErrorBox error={act.error} />
            <Select
              label="Votre décision"
              value={answer.decision}
              onChange={(e) => setAnswer({ ...answer, decision: e.target.value })}
              options={[
                { value: "accordee", label: "J’accorde" },
                { value: "accordee_sous_conditions", label: "J’accorde sous conditions" },
                { value: "refusee", label: "Je refuse" },
              ]}
            />
            <Textarea label="Réponse écrite" required value={answer.response} error={fieldError(act.error, "response")} onChange={(e) => setAnswer({ ...answer, response: e.target.value })} />
            {answer.decision === "accordee_sous_conditions" ? (
              <Textarea label="Conditions" required value={answer.conditions} error={fieldError(act.error, "conditions")} onChange={(e) => setAnswer({ ...answer, conditions: e.target.value })} />
            ) : null}
            <Input label="Valable jusqu’au (facultatif)" type="date" value={answer.valid_until} onChange={(e) => setAnswer({ ...answer, valid_until: e.target.value })} />
            <Checkbox label="Un avenant au bail est nécessaire" checked={answer.requires_amendment} onChange={(v) => setAnswer({ ...answer, requires_amendment: v })} />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

/** Section « Demandes d'autorisation » côté locataire. */
export function TenantAuthorizationsSection({ leaseId }: { leaseId: string }) {
  const path = meLease(leaseId, "/authorization-requests");
  const q = useGet<{ items: any[]; kinds: { value: string; rule_note: string }[]; rappel: string }>(path);
  const invalidate = useInvalidate();
  const [form, setForm] = useState({ kind: "travaux", title: "", description: "" });
  const [files, setFiles] = useState<{ id: string; filename: string }[]>([]);
  const act = useAction();
  const note = q.data?.kinds.find((k) => k.value === form.kind)?.rule_note;
  return (
    <div className="stack">
      <Card title="Nouvelle demande">
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await act.run(() => post(path, { ...form, attachment_ids: files.map((f) => f.id) }), "Demande envoyée à votre bailleur.");
            if (r) {
              setForm({ kind: "travaux", title: "", description: "" });
              setFiles([]);
              invalidate(path);
            }
          }}
        >
          <ErrorBox error={act.error} />
          <Select label="Type de demande" value={form.kind} options={options(AUTH_KINDS)} onChange={(e) => setForm({ ...form, kind: e.target.value })} />
          {note ? <RuleNote>{note}</RuleNote> : null}
          <Input label="Objet" required value={form.title} error={fieldError(act.error, "title")} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Textarea label="Description" required value={form.description} error={fieldError(act.error, "description")} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <FileDrop
            multiple
            label="Joindre un plan, un devis ou une photo"
            onFiles={async (list) => {
              for (const f of list) {
                const d = await act.run(() => sendFile(meLease(leaseId, "/attachments"), f, { category: "justificatif" }));
                if (d) setFiles((xs) => [...xs, { id: d.id, filename: d.filename }]);
              }
            }}
          />
          {files.length ? <p className="small">Pièces : {files.map((f) => f.filename).join(", ")}</p> : null}
          <div>
            <Button type="submit" busy={act.busy}>
              Envoyer la demande
            </Button>
          </div>
        </form>
      </Card>
      <Notice tone="info">{q.data?.rappel ?? "Sans réponse écrite du bailleur, la demande n’est pas accordée."}</Notice>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : (
        (q.data?.items ?? []).map((r) => (
          <RequestCard
            key={r.id}
            r={r}
            actions={
              r.status === "en_attente" ? (
                <div>
                  <Button small variant="discret" onClick={async () => { await act.run(() => post(`${path}/${r.id}/withdraw`, {}), "Demande retirée."); invalidate(path); }}>
                    Retirer la demande
                  </Button>
                </div>
              ) : null
            }
          />
        ))
      )}
    </div>
  );
}
